import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { workplaceApi } from '../../services/api';
import { Workplace } from '../../types';
import WorkplaceDetailModal from '../../components/Workplace/WorkplaceDetailModal';
import {
  STATS_DOCUMENTS,
  WorkplaceWithDocs,
  getWorkplaceDocumentStatus,
  exportWorkplacesToExcel
} from '../../utils/excelExport';
import Swal from 'sweetalert2';
import './StatisticsPage.css';

const StatisticsPage: React.FC = () => {
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dangerFilter, setDangerFilter] = useState<'all' | 'low' | 'dangerous' | 'veryDangerous'>('all');
  const [docFilter, setDocFilter] = useState<'all' | 'missing' | 'complete'>('all');
  const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // İşyerlerini API'den çek
  const fetchWorkplaces = useCallback(async () => {
    try {
      setLoading(true);
      const res = await workplaceApi.getAll();
      if (res.success && res.data) {
        setWorkplaces(res.data);
      }
    } catch (err) {
      console.error('İstatistik verisi alınamadı:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkplaces();
  }, [fetchWorkplaces]);

  // Her işyeri için evrak durumlarını ve ilerlemeyi hesapla
  const workplacesWithDocs: WorkplaceWithDocs[] = useMemo(() => {
    return workplaces.map(wp => getWorkplaceDocumentStatus(wp));
  }, [workplaces, isDetailOpen]);

  // Filtrelenmiş liste
  const filteredItems = useMemo(() => {
    const q = searchTerm.toLocaleLowerCase('tr-TR').trim();

    return workplacesWithDocs.filter(item => {
      const title = (item.workplace.title || '').toLocaleLowerCase('tr-TR');
      const sgk = (item.workplace.sgkNo || '').toLocaleLowerCase('tr-TR');
      const matchesSearch = !q || title.includes(q) || sgk.includes(q);

      const matchesDanger =
        dangerFilter === 'all' || item.workplace.dangerClass === dangerFilter;

      let matchesDoc = true;
      if (docFilter === 'missing') {
        matchesDoc = item.readyDocCount === 0;
      } else if (docFilter === 'complete') {
        matchesDoc = item.readyDocCount > 0;
      }

      return matchesSearch && matchesDanger && matchesDoc;
    });
  }, [workplacesWithDocs, searchTerm, dangerFilter, docFilter]);

  // Toplam Metrikler
  const totalWorkplaces = workplaces.length;
  const totalEmployees = workplaces.reduce((sum, w) => sum + (w.employeeCount || 0), 0);

  // Evrak durumlarına göre işyeri sayıları
  const completedWorkplacesCount = useMemo(() => {
    return workplacesWithDocs.filter(item => item.readyDocCount > 0).length;
  }, [workplacesWithDocs]);

  const missingWorkplacesCount = useMemo(() => {
    return workplacesWithDocs.filter(item => item.readyDocCount === 0).length;
  }, [workplacesWithDocs]);

  const avgProgress = useMemo(() => {
    if (workplacesWithDocs.length === 0) return 0;
    const total = workplacesWithDocs.reduce((sum, item) => sum + item.progress, 0);
    return Math.round(total / workplacesWithDocs.length);
  }, [workplacesWithDocs]);

  const totalReadyDocs = useMemo(() => {
    return workplacesWithDocs.reduce((sum, item) => sum + item.readyDocCount, 0);
  }, [workplacesWithDocs]);

  const totalPossibleDocs = workplaces.length * STATS_DOCUMENTS.length;
  const totalMissingDocs = Math.max(0, totalPossibleDocs - totalReadyDocs);

  // Tehlike Sınıfı Dağılımı
  const lowRiskCount = workplaces.filter(w => w.dangerClass === 'low').length;
  const dangerousCount = workplaces.filter(w => w.dangerClass === 'dangerous').length;
  const veryDangerousCount = workplaces.filter(w => w.dangerClass === 'veryDangerous').length;

  const lowRiskPercent = totalWorkplaces > 0 ? Math.round((lowRiskCount / totalWorkplaces) * 100) : 0;
  const dangerousPercent = totalWorkplaces > 0 ? Math.round((dangerousCount / totalWorkplaces) * 100) : 0;
  const veryDangerousPercent = totalWorkplaces > 0 ? Math.round((veryDangerousCount / totalWorkplaces) * 100) : 0;

  // İSG Destek Formatında Renkli & Dikey Başlıklı Excel İndirme
  const handleExportExcel = () => {
    if (workplacesWithDocs.length === 0) {
      Swal.fire('Bilgi', 'İndirilecek kayıtlı işyeri bulunamadı.', 'info');
      return;
    }
    setIsExporting(true);
    try {
      exportWorkplacesToExcel(
        filteredItems.length > 0 ? filteredItems : workplacesWithDocs,
        'ISG_Uzman_Istatistik_Raporu'
      );
      Swal.fire({
        icon: 'success',
        title: 'Excel İndirildi',
        text: 'Resmi İSG İstatistik ve Evrak Matrisi başarıyla Excel formatında dışa aktarıldı.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Excel export hatası:', err);
      Swal.fire('Hata', 'Excel dosyası oluşturulurken bir hata meydana geldi.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const getDangerBadge = (dangerClass: string) => {
    switch (dangerClass) {
      case 'veryDangerous':
        return { label: 'Çok Tehlikeli', color: '#991b1b', bg: '#fee2e2', border: '#ef4444' };
      case 'dangerous':
        return { label: 'Tehlikeli', color: '#854d0e', bg: '#fef9c3', border: '#eab308' };
      default:
        return { label: 'Az Tehlikeli', color: '#0f766e', bg: '#ccfbf1', border: '#14b8a6' };
    }
  };

  const openDetail = (wp: Workplace) => {
    setSelectedWorkplace(wp);
    setIsDetailOpen(true);
  };

  if (loading) {
    return (
      <div className="stats-page-container loading">
        <div className="stats-spinner"></div>
        <p>İSG İstatistikleri ve Evrak Matrisi Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="stats-page-container">
      <div className="stats-content-box">
        {/* Üst Başlık ve Excel İndir Butonu */}
        <div className="stats-header-bar">
          <div className="stats-title-area">
            <h1 className="stats-main-title">
              <span>📊</span> İSG İstatistik ve Resmi Evrak Takip Raporu
            </h1>
            <p className="stats-sub-title">
              İSG Katip ve 6331 Sayılı Kanun kapsamındaki yasal evrakların firma bazlı matris dökümü ve uyum analitiği.
            </p>
          </div>

          <div className="stats-actions-area">
            <button
              className="stats-export-btn"
              onClick={handleExportExcel}
              disabled={isExporting}
              title="İSG Destek Kurumsal Formatında Renkli Excel İndir"
            >
              <span className="export-icon">📥</span>
              <span className="export-text">
                {isExporting ? 'Hazırlanıyor...' : 'Excel İstatistik Raporu İndir'}
              </span>
            </button>
          </div>
        </div>

        {/* Metrik Kartları (Tek Satırda 6 Kutu: İşyeri, Çalışan, Uyum, Tehlike Sınıfları Kombine Kutu, Hazır Evrak, Eksik Evrak) */}
        <div className="stats-summary-grid">
          <div
            className={`stat-card clickable ${dangerFilter === 'all' && docFilter === 'all' ? 'active-filter' : ''}`}
            onClick={() => {
              setDangerFilter('all');
              setDocFilter('all');
            }}
            title="Tüm işyerlerini listelemek için tıklayın"
          >
            <div className="stat-card-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
              🏢
            </div>
            <div className="stat-card-data">
              <span className="stat-number">{totalWorkplaces}</span>
              <span className="stat-label">Toplam İşyeri</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon" style={{ background: '#f0fdf4', color: '#16a34a' }}>
              👥
            </div>
            <div className="stat-card-data">
              <span className="stat-number">{totalEmployees}</span>
              <span className="stat-label">Toplam Çalışan</span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
              🎯
            </div>
            <div className="stat-card-data">
              <span className="stat-number">%{avgProgress}</span>
              <span className="stat-label">Ortalama Uyum</span>
            </div>
          </div>

          {/* TEHLİKE SINIFLARI TEK BİR KUTUDA */}
          <div className="stat-card danger-combined-card">
            <div className="stat-card-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
              ⚠️
            </div>
            <div className="stat-card-data danger-combined-data">
              <span className="stat-label-combined">Tehlike Sınıfları Dağılımı</span>
              <div className="danger-mini-badges">
                <button
                  type="button"
                  className={`danger-mini-badge low ${dangerFilter === 'low' ? 'active' : ''}`}
                  onClick={() => setDangerFilter(prev => prev === 'low' ? 'all' : 'low')}
                  title="Az Tehlikeli işyerlerini filtrele"
                >
                  <span className="badge-bullet"></span>
                  <span className="badge-name">Az:</span>
                  <strong>{lowRiskCount}</strong>
                  <small>(%{lowRiskPercent})</small>
                </button>

                <button
                  type="button"
                  className={`danger-mini-badge dangerous ${dangerFilter === 'dangerous' ? 'active' : ''}`}
                  onClick={() => setDangerFilter(prev => prev === 'dangerous' ? 'all' : 'dangerous')}
                  title="Tehlikeli işyerlerini filtrele"
                >
                  <span className="badge-bullet"></span>
                  <span className="badge-name">Tehlikeli:</span>
                  <strong>{dangerousCount}</strong>
                  <small>(%{dangerousPercent})</small>
                </button>

                <button
                  type="button"
                  className={`danger-mini-badge verydangerous ${dangerFilter === 'veryDangerous' ? 'active' : ''}`}
                  onClick={() => setDangerFilter(prev => prev === 'veryDangerous' ? 'all' : 'veryDangerous')}
                  title="Çok Tehlikeli işyerlerini filtrele"
                >
                  <span className="badge-bullet"></span>
                  <span className="badge-name">Çok Tehlikeli:</span>
                  <strong>{veryDangerousCount}</strong>
                  <small>(%{veryDangerousPercent})</small>
                </button>
              </div>
            </div>
          </div>

          <div
            className={`stat-card clickable ${docFilter === 'complete' ? 'active-filter complete' : ''}`}
            onClick={() => setDocFilter(prev => prev === 'complete' ? 'all' : 'complete')}
            title="Evrak yüklenmiş olan firmaları filtrele"
          >
            <div className="stat-card-icon" style={{ background: '#dcfce7', color: '#15803d' }}>
              ✅
            </div>
            <div className="stat-card-data">
              <span className="stat-number">{completedWorkplacesCount}</span>
              <span className="stat-label">Tamamlandı ({completedWorkplacesCount} İşyeri)</span>
            </div>
          </div>

          <div
            className={`stat-card clickable ${docFilter === 'missing' ? 'active-filter missing' : ''}`}
            onClick={() => setDocFilter(prev => prev === 'missing' ? 'all' : 'missing')}
            title="Henüz hiç evrak yüklenmemiş firmaları filtrele"
          >
            <div className="stat-card-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
              ❌
            </div>
            <div className="stat-card-data">
              <span className="stat-number">{missingWorkplacesCount}</span>
              <span className="stat-label">Eksik Evrak ({missingWorkplacesCount} İşyeri)</span>
            </div>
          </div>
        </div>

        {/* Filtre ve Arama Alanı */}
        <div className="stats-filter-bar">
          <div className="filter-search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="filter-search-input"
              placeholder="Firma Unvanı veya SGK Sicil No ile matriste ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                ✕
              </button>
            )}
          </div>

          <div className="filter-pills-row">
            <span className="filter-group-label">Tehlike:</span>
            <button
              className={`filter-pill ${dangerFilter === 'all' ? 'active' : ''}`}
              onClick={() => setDangerFilter('all')}
            >
              Tümü ({totalWorkplaces})
            </button>
            <button
              className={`filter-pill low ${dangerFilter === 'low' ? 'active' : ''}`}
              onClick={() => setDangerFilter('low')}
            >
              Az Tehlikeli ({lowRiskCount})
            </button>
            <button
              className={`filter-pill dangerous ${dangerFilter === 'dangerous' ? 'active' : ''}`}
              onClick={() => setDangerFilter('dangerous')}
            >
              Tehlikeli ({dangerousCount})
            </button>
            <button
              className={`filter-pill verydangerous ${dangerFilter === 'veryDangerous' ? 'active' : ''}`}
              onClick={() => setDangerFilter('veryDangerous')}
            >
              Çok Tehlikeli ({veryDangerousCount})
            </button>
          </div>

          <div className="filter-pills-row">
            <span className="filter-group-label">Evrak:</span>
            <button
              className={`filter-pill ${docFilter === 'all' ? 'active' : ''}`}
              onClick={() => setDocFilter('all')}
            >
              Tümü ({totalWorkplaces})
            </button>
            <button
              className={`filter-pill missing ${docFilter === 'missing' ? 'active' : ''}`}
              onClick={() => setDocFilter('missing')}
              title="Henüz hiç evrak yüklenmemiş firmalar"
            >
              ⚠️ Eksik Evrak ({missingWorkplacesCount})
            </button>
            <button
              className={`filter-pill complete ${docFilter === 'complete' ? 'active' : ''}`}
              onClick={() => setDocFilter('complete')}
              title="Evrak yüklenmiş olan firmalar"
            >
              ✅ Tamamlandı ({completedWorkplacesCount})
            </button>
          </div>
        </div>

        {/* ==================================================================
            KAPSAMLI İSG RESMİ EVRAK MATRİSİ TABLOSU (SAYFAYA DÖKME)
            ================================================================== */}
        <div className="stats-matrix-wrapper">
          <div className="matrix-top-info">
            <h3 className="matrix-title">
              <span>📑</span> Yasal İSG Evrak Takip Matrisi ({filteredItems.length} İşyeri)
            </h3>
            <span className="matrix-hint">
              💡 Satırlara veya unvana tıklayarak işyerinin detayını açabilir ve eksik evrakları yükleyebilirsiniz.
            </span>
          </div>

          {filteredItems.length === 0 ? (
            <div className="matrix-empty-state">
              🏢 Filtreleme kriterlerinize uygun işyeri kaydı bulunamadı.
            </div>
          ) : (
            <div className="matrix-scroll-container">
              <table className="stats-matrix-table">
                <thead>
                  <tr>
                    <th className="col-num">#</th>
                    <th className="col-name">İşyeri Unvanı</th>
                    <th className="col-sgk">SGK / DETSİS</th>
                    <th className="col-danger">Tehlike</th>
                    <th className="col-emp">Çalışan</th>
                    <th className="col-progress">İlerleme</th>
                    {/* 15 Resmi İSG Belgesi Başlıkları */}
                    {STATS_DOCUMENTS.map((doc) => (
                      <th key={doc.code} className="col-doc" title={doc.title}>
                        <span className="doc-col-code">{doc.short}</span>
                      </th>
                    ))}
                    <th className="col-act">Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item, idx) => {
                    const wp = item.workplace;
                    const badge = getDangerBadge(wp.dangerClass);

                    return (
                      <tr
                        key={wp.id}
                        className={`matrix-row risk-${(wp.dangerClass || 'low').toLowerCase()}`}
                        onClick={() => openDetail(wp)}
                      >
                        <td className="col-num">{idx + 1}</td>

                        <td className="col-name" title={wp.title}>
                          <div className="row-title-box">
                            <strong className="wp-title-text">{wp.title}</strong>
                            {wp.contactPerson && (
                              <small className="wp-contact-sub" title={`${wp.contactPerson} (${wp.contactPhone || ''})`}>
                                👤 {wp.contactPerson}
                              </small>
                            )}
                          </div>
                        </td>

                        <td className="col-sgk" title={wp.sgkNo}>
                          <span className="sgk-cell-text">{wp.sgkNo || '-'}</span>
                        </td>

                        <td className="col-danger">
                          <span
                            className="matrix-danger-badge"
                            style={{
                              color: badge.color,
                              backgroundColor: badge.bg,
                              borderColor: badge.border
                            }}
                          >
                            {badge.label}
                          </span>
                        </td>

                        <td className="col-emp">
                          <span className="emp-badge">👥 {wp.employeeCount}</span>
                        </td>

                        <td className="col-progress">
                          <div className="progress-cell-wrapper">
                            <div className="progress-mini-bar">
                              <div
                                className="progress-mini-fill"
                                style={{
                                  width: `${item.progress}%`,
                                  backgroundColor:
                                    item.progress >= 60 ? '#16a34a' : item.progress >= 20 ? '#ca8a04' : '#dc2626'
                                }}
                              />
                            </div>
                            <span className="progress-text-percent">%{item.progress}</span>
                          </div>
                        </td>

                        {/* 17 Evrak Durumu: VAR / YOK / MUAF */}
                        {STATS_DOCUMENTS.map((doc) => {
                          const isCancelled = (wp.cancelledDocuments || []).includes(doc.code);
                          const exists = item.docs[doc.code];
                          return (
                            <td
                              key={doc.code}
                              className="col-doc-cell"
                              title={`${wp.title} — ${doc.title}: ${isCancelled ? 'Muaf (İptal)' : exists ? 'Yüklü (VAR)' : 'Eksik (YOK)'}`}
                            >
                              {isCancelled ? (
                                <span className="doc-tick muaf" title={`${doc.title}: Muaf (İptal)`}>Ø</span>
                              ) : exists ? (
                                <span className="doc-tick var" title={`${doc.title}: Yüklü (VAR)`}>✓</span>
                              ) : (
                                <span className="doc-tick yok" title={`${doc.title}: Hazırlanacak (YOK)`}>-</span>
                              )}
                            </td>
                          );
                        })}

                        <td className="col-act" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="matrix-detail-btn"
                            onClick={() => openDetail(wp)}
                            title="İşyeri Detayı & Evrak Yönetimi"
                          >
                            👁️
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* İşyeri Detay & Evrak Yükleme Modalı */}
      {selectedWorkplace && (
        <WorkplaceDetailModal
          workplace={selectedWorkplace}
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedWorkplace(null);
            fetchWorkplaces(); // Geri dönüldüğünde güncelle
          }}
          onUpdate={() => fetchWorkplaces()}
          onEdit={() => {
            setIsDetailOpen(false);
          }}
          onDelete={(deletedId) => {
            setWorkplaces(prev => prev.filter(w => w.id !== deletedId));
            setIsDetailOpen(false);
            setSelectedWorkplace(null);
          }}
        />
      )}
    </div>
  );
};

export default StatisticsPage;
